import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, CheckCircle } from "lucide-react";
import { toast } from "sonner";

interface SubscriptionPlan {
  id: string;
  code: string;
  name: string;
  description: string;
  min_patients: number;
  max_patients: number | null;
  max_file_uploads: number | null;
  price_cents: number;
  features: string[];
  display_order: number;
}

export default function Planos() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    description: "",
    min_patients: 0,
    max_patients: "",
    max_file_uploads: "",
    price_cents: 0,
    features: "",
    display_order: 0,
  });

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    try {
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .order("display_order");

      if (error) throw error;
      setPlans((data || []).map(plan => ({
        id: plan.id,
        code: plan.code,
        name: plan.name,
        description: plan.description || "",
        min_patients: plan.min_patients,
        max_patients: plan.max_patients,
        max_file_uploads: (plan as unknown as { max_file_uploads?: number | null }).max_file_uploads ?? null,
        price_cents: plan.price_cents,
        features: Array.isArray(plan.features) ? plan.features as string[] : [],
        display_order: plan.display_order,
      })));
    } catch (error) {
      console.error("Error loading plans:", error);
      toast.error("Erro ao carregar planos");
    } finally {
      setLoading(false);
    }
  }

  function openEditDialog(plan: SubscriptionPlan) {
    setEditingPlan(plan);
    setFormData({
      code: plan.code,
      name: plan.name,
      description: plan.description || "",
      min_patients: plan.min_patients,
      max_patients: plan.max_patients?.toString() || "",
      max_file_uploads: plan.max_file_uploads?.toString() || "",
      price_cents: plan.price_cents,
      features: plan.features.join("\n"),
      display_order: plan.display_order,
    });
    setIsDialogOpen(true);
  }

  function openCreateDialog() {
    setEditingPlan(null);
    setFormData({
      code: "",
      name: "",
      description: "",
      min_patients: 0,
      max_patients: "",
      max_file_uploads: "",
      price_cents: 0,
      features: "",
      display_order: plans.length,
    });
    setIsDialogOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const planData = {
      code: formData.code,
      name: formData.name,
      description: formData.description,
      min_patients: formData.min_patients,
      max_patients: formData.max_patients ? parseInt(formData.max_patients) : null,
      max_file_uploads: formData.max_file_uploads ? parseInt(formData.max_file_uploads) : null,
      price_cents: formData.price_cents,
      features: formData.features.split("\n").filter((f) => f.trim()),
      display_order: formData.display_order,
    };

    try {
      if (editingPlan) {
        const { error } = await supabase
          .from("subscription_plans")
          .update(planData)
          .eq("id", editingPlan.id);

        if (error) throw error;
        toast.success("Plano atualizado com sucesso");
      } else {
        const { error } = await supabase.from("subscription_plans").insert(planData);

        if (error) throw error;
        toast.success("Plano criado com sucesso");
      }

      setIsDialogOpen(false);
      loadPlans();
    } catch (error) {
      console.error("Error saving plan:", error);
      toast.error("Erro ao salvar plano");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Tem certeza que deseja excluir este plano?")) return;

    try {
      const { error } = await supabase.from("subscription_plans").delete().eq("id", id);

      if (error) throw error;
      toast.success("Plano excluído com sucesso");
      loadPlans();
    } catch (error) {
      console.error("Error deleting plan:", error);
      toast.error("Erro ao excluir plano");
    }
  }

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Planos de Assinatura</h1>
          <p className="text-muted-foreground">Configure os planos disponíveis</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Planos de Assinatura</h1>
          <p className="text-muted-foreground">Configure os planos disponíveis</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Plano
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlan ? "Editar Plano" : "Novo Plano"}</DialogTitle>
              <DialogDescription>
                {editingPlan ? "Edite as informações do plano" : "Crie um novo plano de assinatura"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Código do Plano</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="ex: basic"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ex: Plano Básico"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do plano"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_patients">Mínimo de Pacientes</Label>
                  <Input
                    id="min_patients"
                    type="number"
                    value={formData.min_patients}
                    onChange={(e) => setFormData({ ...formData, min_patients: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_patients">Máximo de Pacientes</Label>
                  <Input
                    id="max_patients"
                    type="number"
                    value={formData.max_patients}
                    onChange={(e) => setFormData({ ...formData, max_patients: e.target.value })}
                    placeholder="Deixe vazio para ilimitado"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_cents">Preço (centavos)</Label>
                  <Input
                    id="price_cents"
                    type="number"
                    value={formData.price_cents}
                    onChange={(e) => setFormData({ ...formData, price_cents: parseInt(e.target.value) })}
                    placeholder="ex: 9900 para R$ 99,00"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_file_uploads">Máximo de Arquivos em Upload Múltiplo</Label>
                <Input
                  id="max_file_uploads"
                  type="number"
                  value={formData.max_file_uploads}
                  onChange={(e) => setFormData({ ...formData, max_file_uploads: e.target.value })}
                  placeholder="1 = sem upload múltiplo, NULL = ilimitado"
                />
                <p className="text-sm text-muted-foreground">
                  Número máximo de arquivos permitidos em upload múltiplo. Deixe vazio para ilimitado. Use 1 para desabilitar upload múltiplo (plano FREE).
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="features">Funcionalidades (uma por linha)</Label>
                <Textarea
                  id="features"
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  placeholder="Lista de funcionalidades&#10;Uma por linha"
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_order">Ordem de Exibição</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  required
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">{editingPlan ? "Salvar" : "Criar"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((plan) => (
          <Card key={plan.id}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {plan.name}
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => openEditDialog(plan)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleDelete(plan.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>{plan.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-3xl font-bold">{formatPrice(plan.price_cents)}</div>
                <div className="text-sm text-muted-foreground">por mês</div>
              </div>

              <div className="space-y-2">
                <div className="text-sm">
                  <strong>Código:</strong> {plan.code}
                </div>
                <div className="text-sm">
                  <strong>Pacientes:</strong> {plan.min_patients} - {plan.max_patients || "ilimitado"}
                </div>
                <div className="text-sm">
                  <strong>Upload Múltiplo:</strong> {plan.max_file_uploads === 1 ? "Não permitido" : plan.max_file_uploads ? `Até ${plan.max_file_uploads} arquivos` : "Ilimitado"}
                </div>
                {plan.features && plan.features.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm font-semibold mb-2">Funcionalidades:</div>
                    <ul className="space-y-1">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
